/**
 * Ponte PURA "mensagem de erro/aviso do popup → aba Erros já filtrada" (pedido
 * 09/09: "ao clicar nessas mensagens sejamos redirecionados para a aba de erros
 * já filtrado direto ... ou com uma animação de foco na linha específica").
 *
 * O contrato é a QUERY STRING — é o mesmo canal que a página já usa para `?tab=`,
 * então a URL resultante é compartilhável e recarregável: quem cola o link cai na
 * aba Erros com o MESMO recorte. Tudo o que dá para decidir sem JSX mora aqui:
 * derivar os parâmetros do item clicado (`filtrosDoAlvo`) e relê-los do outro lado
 * (`lerFiltrosDaQuery`), com `agora`/janela parametrizáveis para teste determinístico.
 *
 * Regra do recorte: o popup NÃO sabe o id da ocorrência (a lista dele vem do
 * `/resumo`, não da auditoria) — o filtro é montado com o que ele tem em mãos
 * (empresa + bot real + status), que já é estreitíssimo, e a aba fica com a JANELA
 * PADRÃO dela (7 dias). Nenhuma ponta de período é derivada do `ts`: a revisão de
 * 09/09 pegou que fatiar o dia com `toISOString()` joga todo evento das 21h-23h59
 * de São Paulo (UTC-3) para o dia SEGUINTE, e a aba abria filtrada no dia errado
 * mostrando "nenhuma ocorrência" — justo no bot noturno. O `ts` sobrevive só como
 * `foco_ts`, que NUNCA filtra: serve para destacar a linha.
 */

import type { StatusAuditoriaFiltro } from '../api';

/**
 * Tolerância do match do foco. O `foco_ts` que o popup manda é APROXIMADO (proxy de
 * `ultima_atividade` da empresa, não o instante daquele erro — `IBotDaEmpresa` não
 * tem timestamp), então casar por igualdade de milissegundo praticamente nunca dava
 * match. Aqui se escolhe o item MAIS PRÓXIMO dentro desta folga.
 */
export const TOLERANCIA_FOCO_MS = 5 * 60000;

/** O que o popup sabe sobre a ocorrência clicada. */
export interface AlvoAuditoria {
  /** Código da empresa da linha clicada (sempre presente no popup). */
  codiEmp: string;
  /** Nome REAL do bot — nunca o apelido de exibição; null quando o popup é de grupo. */
  bot: string | null;
  /** Caixa clicada: vermelha = erro, âmbar = warning. */
  status: 'erro' | 'warning';
  /**
   * Instante APROXIMADO do evento (ISO) — hoje é a `ultima_atividade` da empresa,
   * não o carimbo daquele erro. Vai só como `foco_ts` (destaque), nunca como filtro.
   */
  ts?: string | null;
}

/** Parâmetros de query que a aba Erros entende (todos string, prontos pra URL). */
export interface FiltrosErrosQuery {
  tab: 'erros';
  codi_emp: string;
  status: StatusAuditoriaFiltro;
  bot?: string;
  /** Instante aproximado do evento — usado só para FOCAR a linha, nunca para filtrar. */
  foco_ts?: string;
}

/**
 * Deriva os parâmetros de navegação a partir do item clicado no popup.
 *
 * NENHUMA ponta de período é enviada — nem com `ts` conhecido. O recorte
 * empresa + bot + status já é estreitíssimo, e derivar `de`/`ate` do `ts` era a
 * fonte do bug de fuso (ver cabeçalho): a aba fica com a janela padrão dela e o
 * `foco_ts` cuida do destaque.
 *
 * Bot ausente (popup de grupo, que agrega vários bots) simplesmente não entra:
 * filtrar por um bot arbitrário do grupo esconderia a ocorrência procurada.
 *
 * O STATUS VAI SEMPRE COMO `ambos` (21/09), e esta é a metade da correção que
 * vive no front. O popup pinta "Falha" lendo `bot_unit_result`, mas a lib de
 * 09/2026 grava o fecho dessa unidade como WARNING — mandar `status=erro`
 * fechava a porta para o próprio registro que a pessoa acabou de clicar, mesmo
 * depois de a auditoria passar a ler a fonte nova. Era o segundo motivo de
 * "clico na falha e a aba Erros não acha nada".
 *
 * `ambos` não alarga o recorte de forma perigosa: empresa + bot já é
 * estreitíssimo, e o alvo clicado continua destacado pelo `foco_ts`. Preferir
 * mostrar a ocorrência procurada com uma linha vizinha a não mostrá-la.
 */
export const filtrosDoAlvo = (alvo: AlvoAuditoria): FiltrosErrosQuery => {
  const base: FiltrosErrosQuery = {
    tab: 'erros',
    codi_emp: alvo.codiEmp,
    status: 'ambos',
  };
  if (alvo.bot) base.bot = alvo.bot;
  const instante = alvo.ts ? new Date(alvo.ts) : null;
  if (instante && !Number.isNaN(instante.getTime())) base.foco_ts = alvo.ts as string;
  return base;
};

/** Objeto pronto para `setSearchParams` (só as chaves preenchidas). */
export const queryDoAlvo = (alvo: AlvoAuditoria): Record<string, string> => {
  const filtros = filtrosDoAlvo(alvo);
  const saida: Record<string, string> = {};
  Object.entries(filtros).forEach(([chave, valor]) => {
    if (valor) saida[chave] = String(valor);
  });
  return saida;
};

// ── Leitura do outro lado (inicialização da aba Erros) ───────────────────────

/** Só o que a query string realmente trouxe — o resto fica no default da aba. */
export interface FiltrosIniciaisErros {
  codiEmp: string;
  status: StatusAuditoriaFiltro | null;
  bot: string | null;
  /** Timestamp a destacar na tabela (nunca filtra — só foca). */
  focoTs: string | null;
}

const STATUS_VALIDOS: StatusAuditoriaFiltro[] = ['erro', 'warning', 'ambos'];

/**
 * Lê os filtros que vieram na URL. Entrar SEM parâmetros devolve tudo vazio/null —
 * a aba então mantém os defaults de sempre (7 dias, status "erro"), então quem abre
 * a aba pela barra de abas não vê diferença nenhuma.
 *
 * Não há período nenhum na ponte: a aba mantém a janela padrão dela, e o recorte
 * empresa + bot + status já basta para o registro cair na primeira página.
 */
export const lerFiltrosDaQuery = (params: URLSearchParams): FiltrosIniciaisErros => {
  const status = params.get('status');
  return {
    codiEmp: params.get('codi_emp') ?? '',
    status:
      status && STATUS_VALIDOS.includes(status as StatusAuditoriaFiltro)
        ? (status as StatusAuditoriaFiltro)
        : null,
    bot: params.get('bot') || null,
    focoTs: params.get('foco_ts') || null,
  };
};

// ── Foco da linha ────────────────────────────────────────────────────────────

/** Item mínimo da tabela de auditoria para o match do foco. */
export interface ItemFocavel {
  id: string;
  ts: string | null;
  bot: string;
  codi_emp: string | null;
}

/**
 * Acha, entre os itens da página atual, o que o clique do popup apontava.
 *
 * O `foco_ts` é APROXIMADO (proxy de `ultima_atividade` da empresa — a última
 * atividade dela em QUALQUER bot, não o carimbo daquele erro), então o match é
 * TOLERANTE, nunca por igualdade de milissegundo: dentro do recorte da URL
 * (empresa + bot, quando vieram) escolhe-se o item mais próximo do foco dentro de
 * `toleranciaMs`; se nenhum cair na tolerância, destaca-se o MAIS RECENTE do
 * recorte (a lista chega ordenada do mais novo para o mais antigo, e o item da
 * empresa+bot+status que a pessoa acabou de ver no popup é justamente esse).
 * Recorte vazio → null, e nenhum destaque acontece: melhor sem animação do que
 * destacando a linha de outra empresa.
 */
export const acharItemDoFoco = (
  itens: ItemFocavel[],
  filtros: Pick<FiltrosIniciaisErros, 'focoTs' | 'codiEmp' | 'bot'>,
  toleranciaMs = TOLERANCIA_FOCO_MS,
): string | null => {
  if (!filtros.focoTs) return null;
  const alvo = new Date(filtros.focoTs).getTime();
  if (Number.isNaN(alvo)) return null;
  const doRecorte = itens.filter(it => {
    if (filtros.codiEmp && it.codi_emp !== filtros.codiEmp) return false;
    if (filtros.bot && it.bot !== filtros.bot) return false;
    return true;
  });
  if (!doRecorte.length) return null;

  let melhor: ItemFocavel | null = null;
  let menorDistancia = Infinity;
  doRecorte.forEach(it => {
    if (!it.ts) return;
    const quando = new Date(it.ts).getTime();
    if (Number.isNaN(quando)) return;
    const distancia = Math.abs(quando - alvo);
    if (distancia <= toleranciaMs && distancia < menorDistancia) {
      menorDistancia = distancia;
      melhor = it;
    }
  });
  // `melhor` é reatribuído dentro do forEach; o narrowing do TS não enxerga isso.
  if (melhor) return (melhor as ItemFocavel).id;

  // Nada dentro da tolerância: o mais recente do recorte é o melhor palpite.
  return doRecorte[0].id;
};
